import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays, ShieldAlert, Lock, Trash2, Eye, Send, EyeOff, Repeat, MapPin, Clock, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';
import { usePageMeta } from '@/hooks/usePageMeta';

// Strong warning shown before a permanent DJ-event delete (matches the DJ flow).
const DJ_DELETE_WARNING = 'This permanently removes the event for everyone, including users who saved it.';

function Centered({ children }) {
  return <div className="max-w-4xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>;
}
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}
function GateAdmin({ role }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <h1 className="text-xl font-semibold mb-2">Admin access required</h1>
      <p className="opacity-80">Your role is <span className="capitalize font-medium">{role || 'unknown'}</span>.</p>
    </div>
  );
}

const isDj = (e) => e?.source === 'dj' || e?.dj_submitted;

function statusBadge(s) {
  if (s === 'published') return <Badge className="bg-green-600 capitalize">{s}</Badge>;
  if (s === 'archived') return <Badge variant="outline" className="capitalize">{s}</Badge>;
  return <Badge variant="secondary" className="capitalize">{s || 'draft'}</Badge>;
}

function creatorLabel(e) {
  if (isDj(e)) return 'DJ-submitted';
  if (e.source === 'organizer' || e.owner_type === 'organizer') return 'Organizer';
  return 'Admin / Catalog';
}

// Display the stored wall-clock (DJ events store date + HH:MM separately;
// catalog events embed the time in start_date). No timezone shifting.
function fmtWhen(e) {
  const d = e.start_date;
  if (!d) return '—';
  const s = String(d);
  const datePart = s.slice(0, 10);
  let timePart = e.start_time ? String(e.start_time).slice(0, 5) : '';
  if (!timePart && s.includes('T')) {
    const t = new Date(s.replace('Z', '+00:00'));
    if (!isNaN(t.getTime())) timePart = t.toISOString().slice(11, 16);
  }
  return datePart + (timePart ? ` · ${timePart}` : '');
}

function isPast(e) {
  const date = e.end_date || e.start_date;
  if (!date) return false;
  const time = e.end_time || e.start_time || '23:59';
  const s = String(date);
  const iso = s.includes('T') ? s.replace('Z', '+00:00') : `${s.slice(0, 10)}T${String(time).slice(0, 5)}:00Z`;
  const dt = new Date(iso);
  return !isNaN(dt.getTime()) && dt.getTime() < Date.now();
}

export default function AdminEvents() {
  usePageMeta('Admin · Events', 'Manage admin-created and DJ-submitted events.');
  const [authed] = useState(api.isAuthenticated());
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [creatorFilter, setCreatorFilter] = useState('all');   // all | admin | dj (server-side)
  const [statusFilter, setStatusFilter] = useState('all');     // all | published | draft | archived (client)
  const [tab, setTab] = useState('upcoming');                  // upcoming | past
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null);                // {title,message,danger,confirmLabel,onConfirm}

  const fetchEvents = useCallback(async () => {
    try {
      const qs = creatorFilter === 'all' ? '' : `?creator=${creatorFilter}`;
      const list = await api.get(`/admin/events${qs}`);
      setEvents(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load events');
    }
  }, [creatorFilter]);

  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    api.get('/users/me').then((me) => setRole(me.role)).catch(() => {}).finally(() => setLoading(false));
  }, [authed]);

  useEffect(() => { if (role === 'admin') fetchEvents(); }, [role, fetchEvents]);

  const eid = (e) => e.id || e.event_id;

  const runAction = async (e, fn, successMsg) => {
    setBusyId(eid(e));
    try { await fn(); toast.success(successMsg); await fetchEvents(); }
    catch (err) { toast.error(err.message || 'Action failed'); }
    finally { setBusyId(null); }
  };

  // ----- actions (wired to inspected backend endpoints) -----
  const publishEvent = (e) => runAction(e, () => api.put(`/admin/events/${eid(e)}`, { status: 'published' }), 'Event published');

  const unpublishEvent = (e) => setConfirm({
    title: 'Unpublish event?',
    message: `"${e.title || '(untitled)'}" will be archived and hidden from the public site. You can re-publish it later.`,
    confirmLabel: 'Unpublish',
    onConfirm: () => runAction(e, () => api.put(`/admin/events/${eid(e)}`, { status: 'archived' }), 'Event unpublished (archived)'),
  });

  const deleteEvent = (e) => {
    const dj = isDj(e);
    setConfirm({
      title: dj ? 'Permanently delete DJ event?' : 'Delete event?',
      message: dj
        ? `"${e.title || '(untitled)'}" — ${DJ_DELETE_WARNING}`
        : `"${e.title || '(untitled)'}" will be deleted (or archived if it has saves/lineup).`,
      danger: true,
      confirmLabel: dj ? 'Delete permanently' : 'Delete',
      onConfirm: () => runAction(
        e,
        async () => {
          // DJ events are managed via the DJ endpoints (admin-allowed); catalog/
          // organizer events via the admin events endpoint.
          if (dj) await api.del(`/dj/events/${eid(e)}`);
          else {
            const res = await api.del(`/admin/events/${eid(e)}`);
            if (res?.archived) toast.message('Had related records — archived instead of deleted.');
          }
        },
        dj ? 'Event permanently deleted' : 'Event removed',
      ),
    });
  };

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;
  if (role !== 'admin') return <GateAdmin role={role} />;

  // client-side status + upcoming/past filtering
  const filtered = events.filter((e) => statusFilter === 'all' || (e.status || 'draft') === statusFilter);
  const upcoming = filtered.filter((e) => !isPast(e));
  const past = filtered.filter((e) => isPast(e));
  const shown = tab === 'upcoming' ? upcoming : past;
  const djCount = events.filter(isDj).length;

  const FilterBtn = ({ value, label, current, set }) => (
    <button
      type="button"
      onClick={() => set(value)}
      className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${current === value ? 'bg-primary text-primary-foreground border-primary' : 'border-border/60 hover:border-primary/50'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pt-20 pb-24 md:pb-8">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-1"><CalendarDays className="h-6 w-6" /> Admin · Events</h1>
      <p className="text-sm text-muted-foreground mb-5">
        Manage admin-created and DJ-submitted events. DJ <span className="font-medium">drafts</span> are private to the DJ and not shown here.
      </p>

      {/* Filters */}
      <div className="space-y-3 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide opacity-60 mr-1">Creator</span>
          <FilterBtn value="all" label="All" current={creatorFilter} set={setCreatorFilter} />
          <FilterBtn value="admin" label="Admin-created" current={creatorFilter} set={setCreatorFilter} />
          <FilterBtn value="dj" label="DJ-submitted" current={creatorFilter} set={setCreatorFilter} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide opacity-60 mr-1">Status</span>
          <FilterBtn value="all" label="All" current={statusFilter} set={setStatusFilter} />
          <FilterBtn value="published" label="Published" current={statusFilter} set={setStatusFilter} />
          <FilterBtn value="draft" label="Draft" current={statusFilter} set={setStatusFilter} />
          <FilterBtn value="archived" label="Archived" current={statusFilter} set={setStatusFilter} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-border/50">
        {[['upcoming', `Upcoming (${upcoming.length})`], ['past', `Past (${past.length})`]].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setTab(v)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === v ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Empty states */}
      {events.length === 0 ? (
        <Centered>{creatorFilter === 'dj' ? 'No DJ-submitted events yet.' : 'No events found.'}</Centered>
      ) : shown.length === 0 ? (
        <Centered>{tab === 'upcoming' ? 'No upcoming events.' : 'No past events.'}</Centered>
      ) : (
        <div className="space-y-3">
          {creatorFilter === 'dj' && (
            <p className="text-xs text-muted-foreground">{djCount} DJ-submitted event{djCount !== 1 ? 's' : ''} (published only).</p>
          )}
          {shown.map((e) => {
            const dj = isDj(e);
            const busy = busyId === eid(e);
            const past = isPast(e);
            return (
              <Card key={eid(e)} className={dj ? 'border-primary/30' : 'border-border/50'}>
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    {/* Info */}
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold truncate">{e.title || '(untitled)'}</span>
                        {statusBadge(e.status)}
                        {dj && <Badge className="bg-primary/20 text-primary border-primary/30">DJ-submitted</Badge>}
                        {past && <Badge variant="outline">Past</Badge>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtWhen(e)}</span>
                        {(e.venue || e.city) && (
                          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[e.venue, e.city].filter(Boolean).join(' · ')}</span>
                        )}
                        <span className="inline-flex items-center gap-1">{dj && <Repeat className="h-3.5 w-3.5" />}{creatorLabel(e)}</span>
                      </div>
                      {dj && (e.creator_dj_name || e.creator_dj_slug) && (
                        <div className="text-xs text-muted-foreground">
                          Creator DJ: <span className="font-medium">{e.creator_dj_name || e.creator_dj_slug}</span>
                          {e.creator_dj_slug && <Link to={`/djs/${e.creator_dj_slug}`} className="ml-2 underline hover:text-primary">view profile</Link>}
                        </div>
                      )}
                      <div className="text-[11px] text-muted-foreground/70 break-all">slug: {e.slug || '—'} · id: {eid(e)}</div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {e.status === 'published' && e.slug && (
                        <Link to={`/event/${e.slug}`} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm" disabled={busy}><Eye className="h-4 w-4 mr-1" /> View</Button>
                        </Link>
                      )}
                      {/* Publish / Unpublish — only for catalog/organizer (admin events API).
                          DJ publish/unpublish is omitted: DJ drafts are hidden here, and
                          unpublishing a DJ event would remove it from this list. */}
                      {!dj && e.status !== 'published' && (
                        <Button variant="outline" size="sm" disabled={busy} onClick={() => publishEvent(e)}>
                          <Send className="h-4 w-4 mr-1" /> Publish
                        </Button>
                      )}
                      {!dj && e.status === 'published' && (
                        <Button variant="outline" size="sm" disabled={busy} onClick={() => unpublishEvent(e)}>
                          <EyeOff className="h-4 w-4 mr-1" /> Unpublish
                        </Button>
                      )}
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => deleteEvent(e)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation modal (no destructive one-click action) */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
          <Card className="w-full max-w-md border-border/60">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className={`h-6 w-6 shrink-0 ${confirm.danger ? 'text-red-500' : 'text-amber-500'}`} />
                <div>
                  <h2 className="text-lg font-semibold">{confirm.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{confirm.message}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
                <Button
                  variant={confirm.danger ? 'destructive' : 'default'}
                  onClick={() => { const fn = confirm.onConfirm; setConfirm(null); fn && fn(); }}
                >
                  {confirm.confirmLabel || 'Confirm'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
